<template>
	<div class="plan-view">
		<template v-if="plan">
			<section class="plan-hero">
				<div class="plan-hero__content">
					<div class="plan-hero__eyebrow">
						<v-icon color="white" size="17">verified_user</v-icon>
						<span>PLAN ACTUAL</span>
					</div>
					<div class="plan-hero__heading">
						<div>
							<h1>{{ plan.name || 'Plan sin nombre' }}</h1>
							<p>{{ plan.description || 'Beneficios y límites asignados a tu empresa.' }}</p>
						</div>
						<span class="plan-status" :class="status.className">
							<span class="plan-status__dot"></span>
							{{ status.label }}
						</span>
					</div>
					<div class="plan-hero__meta">
						<div>
							<span>Empresa</span>
							<strong>{{ companyName }}</strong>
						</div>
						<div>
							<span>Código del plan</span>
							<strong>{{ plan.code_plan || 'Sin código' }}</strong>
						</div>
					</div>
				</div>
				<div class="plan-hero__mark" aria-hidden="true">
					<v-icon>account_balance</v-icon>
				</div>
			</section>

			<section class="plan-grid plan-grid--top">
				<article class="plan-card validity-card">
					<header class="plan-card__header">
						<div class="plan-card__icon"><v-icon>event_available</v-icon></div>
						<div>
							<h2>Vigencia del plan</h2>
							<p>Periodo contratado para esta empresa</p>
						</div>
					</header>
					<div class="validity-dates">
						<div>
							<span>Inicio</span>
							<strong>{{ formatDate(plan.start_date) }}</strong>
						</div>
						<v-icon>arrow_forward</v-icon>
						<div>
							<span>Finaliza</span>
							<strong>{{ formatDate(plan.end_date) }}</strong>
						</div>
					</div>
					<div v-if="hasValidity" class="validity-progress">
						<div class="validity-progress__labels">
							<span>Periodo transcurrido</span>
							<strong>{{ validityMessage }}</strong>
						</div>
						<div class="validity-progress__track">
							<span :style="{ width: validityPercentage + '%' }"></span>
						</div>
					</div>
				</article>

				<article v-if="limits.length" class="plan-card summary-card">
					<header class="plan-card__header">
						<div class="plan-card__icon"><v-icon>dashboard</v-icon></div>
						<div>
							<h2>Resumen de capacidad</h2>
							<p>Máximos permitidos por tu plan</p>
						</div>
					</header>
					<div class="capacity-grid">
						<div v-for="item in limits" :key="item.key" class="capacity-item">
							<v-icon>{{ item.icon }}</v-icon>
							<div>
								<strong>{{ item.value }}</strong>
								<span>{{ item.label }}</span>
							</div>
						</div>
					</div>
				</article>
			</section>

			<section v-if="userProfiles.length" class="plan-card plan-section">
				<header class="plan-section__header">
					<div>
						<span class="section-kicker">ACCESOS</span>
						<h2>Usuarios según perfil</h2>
						<p>Perfiles habilitados y cantidad máxima de usuarios.</p>
					</div>
					<span class="section-count">{{ userProfiles.length }} perfiles</span>
				</header>
				<div class="profiles-grid">
					<article v-for="profile in userProfiles" :key="profile.code" class="profile-item">
						<div class="profile-item__icon">
							<v-icon>person</v-icon>
						</div>
						<div class="profile-item__content">
							<strong>{{ profile.name }}</strong>
							<span>{{ profile.code }}</span>
						</div>
						<div class="profile-item__quantity">{{ profile.quantity }}</div>
					</article>
				</div>
			</section>

			<section
				v-if="features.length || additionalServices.length"
				class="plan-grid plan-grid--benefits"
				:class="{ 'plan-grid--single': !features.length || !additionalServices.length }"
			>
				<article v-if="features.length" class="plan-card plan-section">
					<header class="plan-section__header">
						<div>
							<span class="section-kicker">FUNCIONALIDADES</span>
							<h2>Características incluidas</h2>
							<p>Herramientas disponibles dentro del plan.</p>
						</div>
					</header>
					<div class="features-list">
						<div v-for="feature in features" :key="feature.key" class="feature-item">
							<div class="feature-item__icon">
								<v-icon>{{ feature.icon }}</v-icon>
							</div>
							<div>
								<strong>{{ feature.label }}</strong>
								<span>{{ feature.detail }}</span>
							</div>
							<span class="feature-state">
								<v-icon>check_circle</v-icon>
								Incluido
							</span>
						</div>
					</div>
				</article>

				<article v-if="additionalServices.length" class="plan-card plan-section">
					<header class="plan-section__header">
						<div>
							<span class="section-kicker">SERVICIOS</span>
							<h2>Servicios adicionales</h2>
							<p>Integraciones complementarias contratadas.</p>
						</div>
					</header>
					<div class="services-grid">
						<div
							v-for="service in additionalServices"
							:key="service.key"
							class="service-item"
						>
							<div class="service-item__icon"><v-icon>{{ service.icon }}</v-icon></div>
							<div>
								<strong>{{ service.label }}</strong>
								<span>Servicio habilitado</span>
							</div>
							<v-icon class="service-item__state">check_circle</v-icon>
						</div>
					</div>
				</article>
			</section>
		</template>

		<section v-else class="plan-empty">
			<div class="plan-empty__icon">
				<v-icon>assignment_late</v-icon>
			</div>
			<span class="section-kicker">PLAN NO CONFIGURADO</span>
			<h1>Tu empresa todavía no tiene un plan asignado</h1>
			<p>
				Solicita al administrador que asigne un plan a la empresa desde ACL.
				Después, cierra sesión e ingresa nuevamente para actualizar la información.
			</p>
		</section>
	</div>
</template>

<script>
/* eslint-disable camelcase */
const PROFILE_NAMES = {
	super_admin: 'Super Administrador',
	admin: 'Administrador general',
	maki_vendor: 'Vendedores Usuario-Móvil',
	reseller: 'Vendedores de Campo',
	store_keeper: 'Encargado de tienda',
	support: 'Cuenta de soporte Maki',
	self_service: 'Autoservicio',
	monitor: 'Monitoreo Cocina',
	customer_reseller: 'Cliente reseller',
	employee_reseller: 'Empleado reseller',
	test_customer_reseller: 'Cliente reseller de pruebas',
	delivery: 'Empleado Delivery',
	accountant: 'Administrador Contable',
	adminmaki: 'Super Administrador Maki',
	adminfran: 'Administrador de franquicias',
	fran: 'Franquicia Facturador',
	franadmin: 'Administrador por franquicia',
};

function parseJson(value, fallback) {
	if (typeof value !== 'string') {
		return value || fallback;
	}
	try {
		return JSON.parse(value);
	// eslint-disable-next-line no-unused-vars
	} catch (error) {
		return fallback;
	}
}

function normalizePlan(value) {
	const parsedValue = parseJson(value, null);
	const plan = Array.isArray(parsedValue) ? parsedValue[0] : parsedValue;
	if (!plan || typeof plan !== 'object') {
		return null;
	}
	return Object.assign({}, plan, {
		additional_services: parseJson(plan.additional_services, {}),
		users: parseJson(plan.users, {}),
	});
}

function getStoredUser() {
	const storageKey = `${process.env.STORAGE_USER_KEY}::adminUser`;
	return parseJson(localStorage.getItem(storageKey), {});
}

function created() {
	const userInfo = parseJson(this.$userInfo, this.$userInfo);
	const currentUser = userInfo && typeof userInfo === 'object' ? userInfo : getStoredUser();
	const aclCompany = currentUser.companyAcl || {};
	this.currentUser = currentUser;
	this.plan = normalizePlan(
		currentUser.companyPlan || aclCompany.plan || aclCompany.selectPlans,
	);
}

function parseDate(value) {
	if (!value) {
		return null;
	}
	const dateParts = String(value).slice(0, 10).split('-');
	if (dateParts.length !== 3) {
		return null;
	}
	const date = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
	return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
	const date = parseDate(value);
	if (!date) {
		return 'Sin definir';
	}
	return new Intl.DateTimeFormat('es-EC', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	}).format(date);
}

function isEnabled(value) {
	return value === true || value === 1 || value === '1' || value === 'true';
}

function formatLimit(value) {
	if (Number(value) < 0) {
		return 'Sin límite';
	}
	return Number(value).toLocaleString('es-EC');
}

function hasConfiguredLimit(value) {
	if (value === null || typeof value === 'undefined' || value === '') {
		return false;
	}
	const numericValue = Number(value);
	return !Number.isNaN(numericValue) && numericValue !== 0;
}

function humanizeCode(code) {
	return String(code)
		.replace(/_/g, ' ')
		.replace(/\b\w/g, letter => letter.toUpperCase());
}

function companyName() {
	const aclCompany = this.currentUser.companyAcl || {};
	const company = this.currentUser.company || {};
	return aclCompany.razonSocial || aclCompany.razon_social ||
		aclCompany.nombreComercial || aclCompany.nombre_comercial ||
		company.rzSocial || company.rz_social || company.nameCompany || company.name_company ||
		company.name || company.ruc || 'Tu empresa';
}

function status() {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const startDate = parseDate(this.plan.start_date);
	const endDate = parseDate(this.plan.end_date);
	if (endDate && endDate < today) {
		return { className: 'plan-status--expired', label: 'Vencido' };
	}
	if (startDate && startDate > today) {
		return { className: 'plan-status--scheduled', label: 'Próximo' };
	}
	return { className: 'plan-status--active', label: 'Activo' };
}

function hasValidity() {
	return Boolean(parseDate(this.plan.start_date) && parseDate(this.plan.end_date));
}

function validityPercentage() {
	const startDate = parseDate(this.plan.start_date);
	const endDate = parseDate(this.plan.end_date);
	if (!startDate || !endDate || endDate <= startDate) {
		return 0;
	}
	const elapsed = Date.now() - startDate.getTime();
	const duration = endDate.getTime() - startDate.getTime();
	return Math.min(100, Math.max(0, Math.round((elapsed / duration) * 100)));
}

function validityMessage() {
	const endDate = parseDate(this.plan.end_date);
	if (!endDate) {
		return 'Sin fecha final';
	}
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const days = Math.ceil((endDate.getTime() - today.getTime()) / 86400000);
	if (days < 0) {
		return `Venció hace ${Math.abs(days)} días`;
	}
	if (days === 0) {
		return 'Finaliza hoy';
	}
	return `${days} días restantes`;
}

function limits() {
	return [
		{
			icon: 'people',
			key: 'limit_access',
			label: 'Accesos simultáneos',
			value: formatLimit(this.plan.limit_access),
		},
		{
			icon: 'business',
			key: 'subsidiaries_limit',
			label: 'Empresas',
			value: formatLimit(this.plan.subsidiaries_limit),
		},
		{
			icon: 'local_offer',
			key: 'products',
			label: 'Productos',
			value: formatLimit(this.plan.products),
		},
		{ icon: 'store', key: 'warehouses', label: 'Tiendas', value: formatLimit(this.plan.warehouses) },
		{ icon: 'shopping_cart', key: 'orders', label: 'Pedidos', value: formatLimit(this.plan.orders) },
	].filter(item => hasConfiguredLimit(this.plan[item.key]));
}

function userProfiles() {
	const users = this.plan.users || {};
	return Object.keys(users)
		.filter(code => Number(users[code]) > 0)
		.map(code => ({
			code,
			name: PROFILE_NAMES[code] || humanizeCode(code),
			quantity: Number(users[code]).toLocaleString('es-EC'),
		}));
}

function features() {
	return [
		{
			detail: this.plan.electronic_billing_monthly
				? `${this.plan.electronic_billing_monthly} comprobantes mensuales`
				: 'Emisión de comprobantes electrónicos',
			enabled: isEnabled(this.plan.electronic_billing),
			icon: 'receipt',
			key: 'electronic_billing',
			label: 'Facturación electrónica',
		},
		{
			detail: 'Firma para documentos electrónicos',
			enabled: isEnabled(this.plan.electronic_firm),
			icon: 'create',
			key: 'electronic_firm',
			label: 'Firma electrónica',
		},
		{
			detail: 'Reportes incluidos en el plan',
			enabled: isEnabled(this.plan.report),
			icon: 'assessment',
			key: 'report',
			label: 'Reportes',
		},
		{
			detail: 'Carga de documentos XML',
			enabled: isEnabled(this.plan.xml_load),
			icon: 'file_upload',
			key: 'xml_load',
			label: 'Carga de XML',
		},
		{
			detail: 'Control y valoración de inventario',
			enabled: isEnabled(this.plan.kardex),
			icon: 'storage',
			key: 'kardex',
			label: 'Kárdex',
		},
		{
			detail: this.plan.support_type || 'Atención y acompañamiento',
			enabled: Boolean(this.plan.support_type),
			icon: 'headset_mic',
			key: 'support_type',
			label: 'Soporte',
		},
	].filter(feature => feature.enabled);
}

function additionalServices() {
	const services = this.plan.additional_services || {};
	return [
		{ enabled: isEnabled(services.crm), icon: 'people', key: 'crm', label: 'CRM' },
		{ enabled: isEnabled(services.deuna), icon: 'payment', key: 'deuna', label: 'Deuna' },
		{ enabled: isEnabled(services.whatsapp), icon: 'chat', key: 'whatsapp', label: 'WhatsApp' },
		{
			enabled: isEnabled(services.push_notifications),
			icon: 'notifications_active',
			key: 'push_notifications',
			label: 'Notificaciones push',
		},
	].filter(service => service.enabled);
}

function data() {
	return {
		currentUser: {},
		plan: null,
	};
}

export default {
	name: 'your-plan-summary',
	computed: {
		additionalServices,
		companyName,
		features,
		hasValidity,
		limits,
		status,
		userProfiles,
		validityMessage,
		validityPercentage,
	},
	created,
	data,
	methods: {
		formatDate,
	},
};
</script>

<style lang="scss" scoped>
$plan-primary: color(secondary);
$plan-dark: color(background);
$plan-ink: color(black);
$plan-muted: color(text);
$plan-border: color(grey-divider);

.plan-view {
	color: $plan-ink;
	font-family: font(AvenirRegular);
	margin: 0 auto;
	max-width: 1180px;

	.v-icon {
		color: $plan-primary !important;
	}
}

.plan-hero {
	background: linear-gradient(118deg, #075f63 0%, #027f84 58%, #02979b 100%);
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 20px;
	box-shadow: 0 18px 44px rgba(2, 121, 126, 0.2);
	color: white;
	display: flex;
	justify-content: space-between;
	min-height: 220px;
	overflow: hidden;
	padding: 32px 36px;
	position: relative;

	&::after {
		border: 1px solid rgba(255, 255, 255, 0.16);
		border-radius: 50%;
		content: '';
		height: 280px;
		position: absolute;
		right: -80px;
		top: -135px;
		width: 280px;
	}

	.v-icon {
		color: white !important;
	}
}

.plan-hero__content {
	position: relative;
	z-index: 2;
}

.plan-hero__eyebrow {
	align-items: center;
	display: flex;
	font-family: font(AvenirBold);
	font-size: 11px;
	gap: 8px;
	letter-spacing: 1.7px;
	margin-bottom: 18px;
}

.plan-hero__heading {
	align-items: flex-start;
	display: flex;
	gap: 22px;

	h1 {
		background: transparent !important;
		border: 0 !important;
		border-radius: 0 !important;
		box-shadow: none !important;
		color: white !important;
		display: block;
		font-family: font(AvenirBold);
		font-size: 30px;
		line-height: 1.15;
		margin: 0 0 8px;
		padding: 0 !important;
		width: auto !important;
	}

	p {
		color: rgba(255, 255, 255, 0.8);
		font-size: 14px;
		margin: 0;
		max-width: 620px;
	}
}

.plan-status {
	align-items: center;
	background: rgba(255, 255, 255, 0.16);
	border: 1px solid rgba(255, 255, 255, 0.28);
	border-radius: 99px;
	display: inline-flex;
	font-family: font(AvenirDemi);
	font-size: 12px;
	gap: 7px;
	padding: 6px 11px;
	white-space: nowrap;
}

.plan-status__dot {
	background: #8cf2bf;
	border-radius: 50%;
	box-shadow: 0 0 0 4px rgba(140, 242, 191, 0.15);
	height: 7px;
	width: 7px;
}

.plan-status--expired .plan-status__dot {
	background: #ff9a9a;
}

.plan-status--scheduled .plan-status__dot {
	background: #ffe092;
}

.plan-hero__meta {
	display: flex;
	gap: 45px;
	margin-top: 32px;

	div {
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	span {
		color: rgba(255, 255, 255, 0.66);
		font-size: 11px;
	}

	strong {
		font-family: font(AvenirDemi);
		font-size: 14px;
	}
}

.plan-hero__mark {
	align-items: center;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 50%;
	display: flex;
	height: 94px;
	justify-content: center;
	margin: 28px 24px 0 20px;
	position: relative;
	width: 94px;
	z-index: 2;

	.v-icon {
		font-size: 42px;
	}
}

.plan-grid {
	display: grid;
	gap: 18px;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	margin-top: 18px;
}

.plan-grid--top {
	grid-template-columns: 0.9fr 1.1fr;
}

.plan-grid--single {
	grid-template-columns: 1fr;
}

.plan-card {
	background: white;
	border: 1px solid $plan-border;
	border-radius: 16px;
	box-shadow: 0 10px 30px rgba(24, 67, 58, 0.07);
}

.plan-card__header {
	align-items: center;
	display: flex;
	gap: 12px;
	padding: 22px 24px 16px;

	h2 {
		font-family: font(AvenirBold);
		font-size: 16px;
		margin: 0 0 2px;
	}

	p {
		color: $plan-muted;
		font-size: 11px;
		margin: 0;
	}
}

.plan-card__icon {
	align-items: center;
	background: rgba(2, 151, 155, 0.1);
	border-radius: 10px;
	display: flex;
	height: 40px;
	justify-content: center;
	width: 40px;

	.v-icon {
		color: $plan-primary !important;
		font-size: 21px;
	}
}

.validity-dates {
	align-items: center;
	display: grid;
	gap: 12px;
	grid-template-columns: 1fr auto 1fr;
	padding: 6px 24px 18px;

	div {
		background: #f6f9f9;
		border-radius: 9px;
		display: flex;
		flex-direction: column;
		padding: 12px 14px;
	}

	span {
		color: $plan-muted;
		font-size: 10px;
		margin-bottom: 2px;
	}

	strong {
		font-family: font(AvenirDemi);
		font-size: 13px;
	}

	> .v-icon {
		color: #8fa49e !important;
		font-size: 18px;
	}
}

.validity-progress {
	border-top: 1px solid #edf2f2;
	padding: 15px 24px 22px;
}

.validity-progress__labels {
	display: flex;
	font-size: 11px;
	justify-content: space-between;
	margin-bottom: 8px;

	span {
		color: $plan-muted;
	}

	strong {
		color: $plan-primary;
		font-family: font(AvenirDemi);
	}
}

.validity-progress__track {
	background: #e8f0f0;
	border-radius: 99px;
	height: 7px;
	overflow: hidden;

	span {
		background: linear-gradient(90deg, $plan-primary, #45c7bd);
		border-radius: inherit;
		display: block;
		height: 100%;
	}
}

.capacity-grid {
	display: grid;
	gap: 10px;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	padding: 6px 24px 24px;
}

.capacity-item {
	align-items: center;
	background: #f5f9f7;
	border: 1px solid #edf2f2;
	border-radius: 9px;
	display: flex;
	gap: 9px;
	min-height: 58px;
	padding: 10px;

	> .v-icon {
		color: $plan-primary !important;
		font-size: 20px;
	}

	div {
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	strong {
		font-family: font(AvenirBold);
		font-size: 13px;
		line-height: 1.2;
	}

	span {
		color: $plan-muted;
		font-size: 9px;
		line-height: 1.2;
		margin-top: 3px;
	}
}

.plan-section {
	margin-top: 18px;
	padding: 24px;
}

.plan-grid .plan-section {
	margin-top: 0;
}

.plan-section__header {
	align-items: flex-start;
	display: flex;
	justify-content: space-between;
	margin-bottom: 18px;

	h2 {
		font-family: font(AvenirBold);
		font-size: 18px;
		margin: 3px 0;
	}

	p {
		color: $plan-muted;
		font-size: 11px;
		margin: 0;
	}
}

.section-kicker {
	color: $plan-primary;
	font-family: font(AvenirBold);
	font-size: 9px;
	letter-spacing: 1.4px;
}

.section-count {
	background: rgba(2, 151, 155, 0.1);
	border-radius: 99px;
	color: $plan-dark;
	font-family: font(AvenirDemi);
	font-size: 10px;
	padding: 6px 10px;
}

.profiles-grid {
	display: grid;
	gap: 10px;
	grid-template-columns: repeat(3, minmax(0, 1fr));
}

.profile-item {
	align-items: center;
	background: #f8fbfa;
	border: 1px solid #dfeae6;
	border-radius: 12px;
	display: flex;
	gap: 10px;
	min-width: 0;
	padding: 11px;
}

.profile-item__icon {
	align-items: center;
	background: rgba(2, 151, 155, 0.1);
	border-radius: 8px;
	display: flex;
	height: 34px;
	justify-content: center;
	min-width: 34px;

	.v-icon {
		color: $plan-primary !important;
		font-size: 18px;
	}
}

.profile-item__content {
	display: flex;
	flex: 1;
	flex-direction: column;
	min-width: 0;

	strong {
		font-family: font(AvenirDemi);
		font-size: 11px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	span {
		color: $plan-muted;
		font-size: 9px;
		margin-top: 2px;
	}
}

.profile-item__quantity {
	align-items: center;
	background: $plan-dark;
	border-radius: 8px;
	color: white;
	display: flex;
	font-family: font(AvenirBold);
	font-size: 12px;
	height: 30px;
	justify-content: center;
	min-width: 30px;
}

.inline-empty {
	align-items: center;
	background: #f7f9f9;
	border-radius: 10px;
	color: $plan-muted;
	display: flex;
	font-size: 12px;
	gap: 9px;
	justify-content: center;
	padding: 24px;
}

.features-list {
	display: grid;
	gap: 10px;
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.feature-item {
	align-items: center;
	background: #f8fbfa;
	border: 1px solid #e0ebe7;
	border-radius: 12px;
	display: flex;
	gap: 11px;
	min-height: 72px;
	padding: 12px;

	> div:nth-child(2) {
		display: flex;
		flex: 1;
		flex-direction: column;
	}

	strong {
		font-family: font(AvenirDemi);
		font-size: 12px;
	}

	span:not(.feature-state) {
		color: $plan-muted;
		font-size: 10px;
	}
}

.feature-item__icon {
	align-items: center;
	background: #e4f2ed;
	border-radius: 8px;
	display: flex;
	height: 35px;
	justify-content: center;
	min-width: 35px;

	.v-icon {
		color: $plan-primary !important;
		font-size: 18px;
	}
}

.feature-state {
	align-items: center;
	background: #e2f3eb;
	border-radius: 99px;
	color: #176a50;
	display: inline-flex;
	font-size: 9px;
	gap: 4px;
	padding: 5px 8px;

	.v-icon {
		color: #21865f !important;
		font-size: 13px;
	}
}

.services-grid {
	display: grid;
	gap: 10px;
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.service-item {
	align-items: center;
	background: #f4faf7;
	border: 1px solid #cfe3dc;
	border-radius: 12px;
	display: flex;
	gap: 10px;
	min-height: 68px;
	padding: 11px;

	> div:nth-child(2) {
		display: flex;
		flex: 1;
		flex-direction: column;
	}

	strong {
		font-family: font(AvenirDemi);
		font-size: 12px;
	}

	span {
		color: $plan-muted;
		font-size: 9px;
	}
}

.service-item__icon {
	align-items: center;
	background: white;
	border-radius: 8px;
	display: flex;
	height: 38px;
	justify-content: center;
	width: 38px;

	.v-icon {
		color: $plan-primary !important;
		font-size: 20px;
	}
}

.service-item__state {
	color: #21865f !important;
	font-size: 18px;
}

.plan-empty {
	align-items: center;
	background: white;
	border: 1px solid $plan-border;
	border-radius: 16px;
	box-shadow: 0 12px 34px rgba(31, 69, 78, 0.07);
	display: flex;
	flex-direction: column;
	justify-content: center;
	min-height: 480px;
	padding: 40px;
	text-align: center;

	h1 {
		font-family: font(AvenirBold);
		font-size: 25px;
		margin: 8px 0;
	}

	p {
		color: $plan-muted;
		font-size: 13px;
		line-height: 1.6;
		margin: 0;
		max-width: 570px;
	}
}

.plan-empty__icon {
	align-items: center;
	background: rgba(2, 151, 155, 0.1);
	border-radius: 18px;
	display: flex;
	height: 72px;
	justify-content: center;
	margin-bottom: 18px;
	width: 72px;

	.v-icon {
		color: $plan-primary !important;
		font-size: 36px;
	}
}

@media (max-width: 1000px) {
	.plan-grid,
	.plan-grid--top {
		grid-template-columns: 1fr;
	}

	.profiles-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 680px) {
	.plan-hero {
		padding: 25px 22px;
	}

	.plan-hero__heading {
		flex-direction: column;

		h1 {
			font-size: 27px;
		}
	}

	.plan-hero__meta {
		flex-direction: column;
		gap: 13px;
		margin-top: 26px;
	}

	.plan-hero__mark {
		display: none;
	}

	.capacity-grid,
	.profiles-grid,
	.features-list,
	.services-grid {
		grid-template-columns: 1fr;
	}

	.plan-section {
		padding: 20px 16px;
	}

	.validity-dates {
		grid-template-columns: 1fr;

		> .v-icon {
			display: none;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/components/your-plan/your-plan-summary.vue